import {
	Box,
	Spinner,
	ShadowRoot,
	Icons,
	Text,
	Inline,
	CheckboxInput,
	Button,
	ButtonOwnProps,
	useId,
	Alert,
	Popover,
	Stack,
	Select,
	Divider,
	Col,
	Grid,
} from "@sembark-travel/ui-base"
import { Dialog } from "@sembark-travel/ui-dialog"
import {
	CheckboxInputField,
	Form,
	SelectField,
	SubmissionError,
	SwitchInputField,
	TextInputField,
	withServerErrors,
	GetFieldValue,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import React, { useMemo, useState } from "react"
import {
	CopyToClipboardButton,
	CopyToClipboard,
} from "@sembark-travel/ui-copy-to-clipboard"
import useSWR from "swr"
import { useSynedStorageState } from "./../storage"
import config from "../config"
import { Link, XHRLink } from "@sembark-travel/ui-router"
import { useXHR } from "@sembark-travel/xhr"
import { generatePath } from "../router-utils"
import { useAuthUser } from "../Auth"
import { SelectOutboundMailbox } from "../Integrations"
import { isTruthy } from "remeda"

export type TEmailInclusionOptions = Array<{
	name: string
	included: 1 | 0
	options?: Array<{
		id?: number
		name: string
		included: 1 | 0
		disabled?: boolean
	}>
	disabled?: boolean
	multiselect?: boolean
}>
function inclusionsBuilder(
	inclusionOptions: TEmailInclusionOptions
): string | null {
	if (!inclusionOptions?.length) return null
	const result: string[] = []
	for (const option of inclusionOptions) {
		if (!option.included) continue
		if (option.multiselect && option.options?.length) {
			const selectedIds = option.options
				.filter((o) => o.included)
				.map((opt) => opt.id)

			if (selectedIds.length) {
				result.push(`${option.name}:${selectedIds.join("|")}`)
			}
			continue
		}
		result.push(option.name)
	}

	return result.join(",")
}

export type TAttachment = "pdf" | "word"

export type TEmailComposer = {
	to: Array<{ email: string; name: string | null }>
	cc: Array<{ email: string; name: string | null }>
	subject: string
	is_reply: boolean
	suggested_mailbox: { id: number; name: string; email: string } | null
	available_attachments?: Array<TAttachment>
	default_attachments?: Array<TAttachment>
	/** Known byte size per attachment type (only for stored docs, e.g. pdf). */
	attachment_sizes?: Partial<Record<TAttachment, number>>
	/** Combined attachment byte cap; oversized docs are blocked/disabled. */
	max_attachment_bytes?: number
	has_email_signature?: boolean
}

function getSelectedEmailOptionNames(
	options: TEmailInclusionOptions,
	included: boolean
): Array<string> {
	return options
		.filter((option) => Boolean(option.included) === included)
		.map((option) => option.name)
}

export function EmailContent({
	maxWidth,
	copyTrackId,
	fetchContent,
	contentCacheKey,
	inclusionOptionsCacheKey,
	defaultInclusionOptions = [],
	copyLabel = "Copy to Clipboard",
	actions,
	paddingY,
	copyBtnLevel,
	downloadUrl,
	downloadUrlLabel = "Word",
	pdfUrl,
	disabled,
	hideContentOnWarning,
	actionOnWarningBanner,
	sendEmailUrl,
	sendEmailTitle,
	sendEmailSuccessMessage,
	onEmailSent,
}: {
	/**
	 * Set the max width of the email content and toolbar
	 */
	maxWidth?: string
	/**
	 * trackId used when copy-to-clipboard happens
	 */
	copyTrackId: string
	/**
	 * Fetcher to get the email content
	 */
	fetchContent: (params: {
		timezone_offset: number
		inclusions: string | null
		exclusions: string | null
	}) => Promise<
		| ({
				content: string
				options: TEmailInclusionOptions
				warning?: string
		  } & Partial<TEmailComposer>)
		| undefined
	>
	/**
	 * Key for caching email content response
	 */
	contentCacheKey?: string
	/**
	 * Key to store email inclusion options to local storage
	 */
	inclusionOptionsCacheKey?: string
	/**
	 * Default value of inclusion options
	 * @default []
	 */
	defaultInclusionOptions?: TEmailInclusionOptions
	/**
	 * Custom label for copy-to-clipboard action
	 * @default Copy to Clipboard
	 */
	copyLabel?: string
	actions?: (props: {
		refresh: () => void
		inclusions: string | null
		exclusions: string | null
		content?: string
		warning?: string
		composer?: Partial<TEmailComposer>
	}) => React.ReactNode
	paddingY?: React.ComponentProps<typeof Box>["paddingY"]
	copyBtnLevel?: ButtonOwnProps["level"]
	downloadUrl?: string
	downloadUrlLabel?: string
	pdfUrl?: string
	disabled?: boolean
	hideContentOnWarning?: boolean
	actionOnWarningBanner?: () => React.ReactNode
	/**
	 * Enable the built-in "Send Email" action. When set, the model's email
	 * endpoint must return composer fields (to, cc, subject, is_reply,
	 * suggested_mailbox) and this is the POST endpoint that sends the email.
	 */
	sendEmailUrl?: string
	/** Dialog title for the built-in send action. @default Send Email */
	sendEmailTitle?: string
	/** Snackbar message after a successful send. */
	sendEmailSuccessMessage?: string
	/** Called after a successful send (in addition to refreshing the content). */
	onEmailSent?: () => void
}) {
	const id = useId()
	const [inclusionOptions, onChangeInclusionOptions] =
		useSynedStorageState<TEmailInclusionOptions>(
			inclusionOptionsCacheKey,
			defaultInclusionOptions
		)
	const { inclusions, exclusions } = useMemo(() => {
		const excludedOptions = getSelectedEmailOptionNames(inclusionOptions, false)
		return {
			inclusions: inclusionOptions.length
				? inclusionsBuilder(inclusionOptions)
				: null,
			exclusions: excludedOptions.length ? excludedOptions.join(",") : null,
		}
	}, [inclusionOptions])
	const {
		data: emailContent,
		isValidating,
		mutate: refresh,
		error,
	} = useSWR(
		`${contentCacheKey || id}&inc=${inclusions}&exc=${exclusions}`,
		() =>
			fetchContent({
				timezone_offset: config.timezoneOffset,
				inclusions: inclusions,
				exclusions: exclusions,
			}).then((data) => {
				if (data?.options) {
					onChangeInclusionOptions(data.options || [])
				}
				return data
			})
	)
	const { content, warning } = emailContent || {}
	const composer: Partial<TEmailComposer> | undefined = emailContent
		? {
				to: emailContent.to,
				cc: emailContent.cc,
				subject: emailContent.subject,
				is_reply: emailContent.is_reply,
				suggested_mailbox: emailContent.suggested_mailbox,
				available_attachments: emailContent.available_attachments,
				default_attachments: emailContent.default_attachments,
				attachment_sizes: emailContent.attachment_sizes,
				max_attachment_bytes: emailContent.max_attachment_bytes,
				has_email_signature: emailContent.has_email_signature,
			}
		: undefined
	return (
		<CopyToClipboard<HTMLDivElement>>
			{({ nodeToCopy, copy }) => (
				<Box bgColor="white" position="relative" key={copyTrackId}>
					<Box position="sticky" top="0" zIndex="10">
						{inclusionOptions?.length || warning ? (
							<Box
								paddingY="4"
								borderBottomWidth="1"
								bgColor={warning ? "warning" : "subtle"}
							>
								{warning ? (
									<Box style={{ maxWidth }} marginX="auto" paddingX="4">
										<Inline
											justifyContent="between"
											gap="4"
											flexWrap="wrap"
											alignItems="center"
										>
											<Inline gap="4">
												<Box>
													<Icons.Attention size="6" color="warning" />
												</Box>
												<Text color="warning" fontWeight="semibold">
													{warning}
												</Text>
											</Inline>
											{actionOnWarningBanner?.()}
										</Inline>
									</Box>
								) : !inclusionOptions.length ? (
									<Box borderTopWidth="1"></Box>
								) : (
									<Inline
										gap="4"
										style={{ maxWidth }}
										marginX="auto"
										paddingX="4"
										flexWrap={"wrap"}
									>
										{inclusionOptions.map((option, index) => {
											const optionId = option.name
												.replace(/\s/g, "_")
												.toLowerCase()
											return (
												<Inline key={option.name} gap="1" alignItems="center">
													<CheckboxInput
														checked={Boolean(option.included)}
														id={`${optionId}_checkbox`}
														disabled={option.disabled}
														onChange={() =>
															onChangeInclusionOptions(
																inclusionOptions.map((o, i) => ({
																	...o,
																	included:
																		i === index
																			? o.included
																				? 0
																				: 1
																			: o.included,
																	options:
																		i === index
																			? o.included
																				? []
																				: o.options?.map((opt) => ({
																						...opt,
																						included: 1,
																					}))
																			: o.options,
																}))
															)
														}
													/>
													{option.options &&
													Number(option.options?.length || 0) > 1 ? (
														<Box position="relative">
															{!option.multiselect && (
																<Box
																	as="label"
																	htmlFor={`${optionId}_select_option`}
																>
																	{option.name} <Icons.ChevronDown />
																</Box>
															)}
															{!option.multiselect ? (
																<Box
																	position="absolute"
																	overflow="hidden"
																	inset="0"
																	title="Select More options"
																>
																	<select
																		value={option.name}
																		id={`${optionId}_select_option`}
																		style={{
																			background: "transparent",
																			opacity: 0,
																		}}
																		onChange={(e) => {
																			const value = e.currentTarget.value
																			onChangeInclusionOptions(
																				inclusionOptions.map((o, i) => ({
																					...o,
																					// update the name to selected value
																					name: i === index ? value : o.name,
																					// always set included to 1
																					included:
																						i === index ? 1 : o.included,
																				}))
																			)
																		}}
																	>
																		{option.options.map((o) => (
																			<option
																				value={o.name}
																				key={o.name}
																				disabled={o.disabled}
																			>
																				{o.name}
																			</option>
																		))}
																	</select>
																</Box>
															) : (
																<Popover
																	trigger={"click"}
																	placement="bottom"
																	content={({ hide }) => (
																		<Stack
																			gap="1"
																			paddingY="4"
																			style={{
																				minHeight: "150px",
																				minWidth: "350px",
																			}}
																			paddingX="6"
																		>
																			<Form<{
																				selected_options: Array<{
																					id?: number
																					name: string
																					included: 1 | 0
																					disabled?: boolean
																				}>
																			}>
																				onSubmit={({ selected_options }) => {
																					onChangeInclusionOptions(
																						inclusionOptions.map((o, i) => ({
																							...o,
																							options:
																								i === index
																									? o.options?.map((opt) => ({
																											...opt,
																											included:
																												selected_options.some(
																													(s) => s.id === opt.id
																												)
																													? 1
																													: 0,
																										})) || []
																									: o.options,
																							included:
																								i === index ? 1 : o.included,
																						}))
																					)
																					hide()
																				}}
																				initialValues={{
																					selected_options:
																						inclusionOptions[
																							index
																						]?.options?.filter(
																							(o) => o.included
																						) || [],
																				}}
																			>
																				{({ handleSubmit }) => {
																					return (
																						<form
																							noValidate
																							onSubmit={handleSubmit}
																						>
																							<Stack gap="2">
																								<SelectField
																									size="sm"
																									name="selected_options"
																									options={option.options}
																									select={Select}
																									multiple
																									label={option.name}
																								/>
																								<GetFieldValue<
																									Array<{
																										id?: string | undefined
																										name: string
																										included: 0 | 1
																										disabled?: boolean
																									}>
																								> name="selected_options">
																									{({ value }) =>
																										value?.length === 0 && (
																											<Inline
																												style={{
																													minHeight: "100px",
																													minWidth: "100%",
																												}}
																												backgroundColor="subtle"
																												rounded="md"
																												justifyContent="center"
																												alignItems="center"
																											>
																												<Text
																													fontSize="sm"
																													color="muted"
																												>
																													No Option Selected
																												</Text>
																											</Inline>
																										)
																									}
																								</GetFieldValue>
																							</Stack>
																							<Divider sm />
																							<Inline gap="4">
																								<Button
																									type="submit"
																									level="primary"
																									size="sm"
																								>
																									Save
																								</Button>
																								<Button
																									size="sm"
																									onClick={() => hide()}
																								>
																									Cancel
																								</Button>
																							</Inline>
																						</form>
																					)
																				}}
																			</Form>
																		</Stack>
																	)}
																>
																	<Box
																		as="label"
																		htmlFor={`${optionId}_select_option`}
																	>
																		{(() => {
																			const selectedOptions =
																				inclusionOptions[index].options?.filter(
																					(o) => Boolean(o.included)
																				) || []

																			if (selectedOptions.length > 1) {
																				return `${selectedOptions[0].name} + ${
																					selectedOptions.length - 1
																				}`
																			}

																			if (selectedOptions.length === 1) {
																				return selectedOptions[0].name
																			}
																			return option.name
																		})()}{" "}
																		<Icons.ChevronDown />
																	</Box>
																</Popover>
															)}
															<Icons.NewBadge />
														</Box>
													) : (
														<label htmlFor={`${optionId}_checkbox`}>
															{option.name}
														</label>
													)}
												</Inline>
											)
										})}
									</Inline>
								)}
							</Box>
						) : (
							<Box borderTopWidth="1"></Box>
						)}
						{content || error ? (
							<Box
								position="absolute"
								right="0"
								style={{ top: "100%" }}
								padding="2"
								bgColor="default"
								roundedBottomLeft="md"
								borderLeftWidth="1"
								borderBottomWidth="1"
							>
								<Inline gap="4" alignItems="center">
									{warning && hideContentOnWarning ? null : (
										<Button
											onClick={() => refresh()}
											disabled={isValidating}
											size="sm"
											level="tertiary"
										>
											<Icons.Refresh
												size="4"
												spin={isValidating}
												opacity="70"
											/>
										</Button>
									)}

									{!error && content ? (
										<>
											{!warning && !disabled ? (
												<Inline gap="2" alignItems="center">
													<CopyToClipboardButton
														level={copyBtnLevel || "primary"}
														size="sm"
														disabled={isValidating}
														onClick={async () => {
															return copy()
														}}
													>
														<Icons.ClipboardCopy /> {copyLabel}
													</CopyToClipboardButton>
													{pdfUrl ? (
														<Button
															size="sm"
															level="tertiary"
															as={XHRLink}
															href={pdfUrl}
															download
															target="_blank"
															query={{
																_pdf: 1,
																timezone_offset: config.timezoneOffset,
																inclusions,
																exclusions,
															}}
															title="Download PDF File"
														>
															<Icons.DocumentDownload /> PDF
														</Button>
													) : null}
													{downloadUrl ? (
														<Button
															size="sm"
															level="tertiary"
															as={XHRLink}
															href={downloadUrl}
															query={{
																_doc: 1,
																timezone_offset: config.timezoneOffset,
																inclusions,
																exclusions,
															}}
															title={`Download ${downloadUrlLabel} File`}
														>
															<Icons.DocumentDownload /> {downloadUrlLabel}
														</Button>
													) : null}
												</Inline>
											) : null}
											{sendEmailUrl && !warning && !disabled ? (
												<SendEmailDialog
													url={sendEmailUrl}
													composer={composer}
													inclusions={inclusions}
													exclusions={exclusions}
													title={sendEmailTitle}
													successMessage={sendEmailSuccessMessage}
													onSent={() => {
														refresh()
														onEmailSent?.()
													}}
												/>
											) : null}
											{actions
												? actions({
														refresh: () => refresh(),
														inclusions,
														exclusions,
														composer,
													})
												: null}
										</>
									) : null}
								</Inline>
							</Box>
						) : null}
					</Box>
					{error ? (
						<Alert status="error">
							{error.message ||
								"Something went wrong!!. Please try again later."}
						</Alert>
					) : null}
					{content ? (
						warning && hideContentOnWarning ? null : (
							<Box
								overflow="auto"
								paddingY={paddingY === undefined ? "12" : paddingY}
								marginX="auto"
								paddingX="4"
								style={{
									maxWidth,
									color: "black",
								}}
								cursor={isValidating ? "wait" : undefined}
							>
								<ShadowRoot>
									<Box
										ref={nodeToCopy}
										style={{
											margin: 0,
											padding: 0,
										}}
										dangerouslySetInnerHTML={{
											__html: content,
										}}
									/>
								</ShadowRoot>
							</Box>
						)
					) : !error ? (
						<Box style={{ minHeight: "500px" }}>
							<Spinner alignCenter padding="4" />
						</Box>
					) : null}
				</Box>
			)}
		</CopyToClipboard>
	)
}

const ATTACHMENT_LABELS: Record<TAttachment, string> = {
	pdf: "PDF",
	word: "Word document",
}

function formatBytes(bytes: number): string {
	const mb = bytes / (1024 * 1024)
	if (mb >= 1) {
		return `${Math.round(mb * 10) / 10} MB`
	}
	return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

/**
 * Generic "Send Email" action for any mailable model. Driven entirely by the
 * `composer` payload (recipient, CC, reply-aware subject, default mailbox) that
 * the model's email endpoint returns, and posts to `url` with the inclusion /
 * exclusion toggles. Returns null when the tenant has no outbound mailbox.
 */
function SendEmailDialog({
	url,
	composer,
	inclusions,
	exclusions,
	title = "Send Email",
	successMessage = "Email sent successfully.",
	onSent,
}: {
	/** POST endpoint that sends the email for the underlying model. */
	url: string
	composer?: Partial<TEmailComposer>
	inclusions: string | null
	exclusions: string | null
	title?: string
	successMessage?: string
	onSent?: () => void
}) {
	const { user } = useAuthUser()
	const [isOpen, setIsOpen] = useState(false)
	const hasConnectedOutboundMailboxes =
		(user?.outbound_mailboxes_count || 0) > 0

	if (!hasConnectedOutboundMailboxes) {
		return null
	}

	return (
		<>
			<Button size="sm" level="primary" onClick={() => setIsOpen(true)}>
				<Icons.Mail /> Send Email
			</Button>
			<Dialog open={isOpen} onClose={() => setIsOpen(false)} title={title}>
				<Dialog.Body>
					{isOpen ? (
						<SendEmailForm
							url={url}
							composer={composer}
							inclusions={inclusions}
							exclusions={exclusions}
							successMessage={successMessage}
							onCancel={() => setIsOpen(false)}
							onSent={() => {
								setIsOpen(false)
								onSent?.()
							}}
						/>
					) : null}
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function SendEmailForm({
	url,
	composer,
	inclusions,
	exclusions,
	successMessage,
	onCancel,
	onSent,
}: {
	url: string
	composer?: Partial<TEmailComposer>
	inclusions: string | null
	exclusions: string | null
	successMessage: string
	onCancel: () => void
	onSent: () => void
}) {
	const xhr = useXHR()
	const { user } = useAuthUser()
	const canChooseMailbox = (user?.outbound_mailboxes_count || 0) > 1
	const [isEditingSubject, setIsEditingSubject] = useState(false)

	const to = composer?.to?.[0]
	const isReply = Boolean(composer?.is_reply)
	const availableAttachments = composer?.available_attachments ?? []
	const attachmentSizes = composer?.attachment_sizes ?? {}
	const maxAttachmentBytes = composer?.max_attachment_bytes
	const isOversizedAttachment = (type: TAttachment): boolean => {
		const size = attachmentSizes[type]
		return (
			size != null && maxAttachmentBytes != null && size > maxAttachmentBytes
		)
	}
	const hasSignature = Boolean(composer?.has_email_signature)

	return (
		<Stack gap="4">
			<Text fontSize="sm" color="muted">
				Review the recipients and subject, then send this email.
			</Text>
			<Form<{
				mailbox: { id: number; name: string; email: string } | null
				subject: string
				cc: string
				attachments: Array<TAttachment>
				include_signature: boolean
			}>
				initialValues={{
					mailbox: composer?.suggested_mailbox ?? null,
					subject: composer?.subject ?? "",
					cc: (composer?.cc ?? []).map((c) => c.email).join(", "),
					attachments: (composer?.default_attachments ?? []).filter(
						(t) => !isOversizedAttachment(t)
					),
					include_signature: hasSignature,
				}}
				onSubmit={withServerErrors(async (values) => {
					await xhr.post(url, {
						mailbox: values.mailbox?.id,
						subject: values.subject,
						cc: values.cc,
						attachments: values.attachments.filter(
							(t) => !isOversizedAttachment(t)
						),
						include_signature: isTruthy(values.include_signature) ? 1 : 0,
						inclusions,
						exclusions,
					})
					showSnackbar(successMessage)
					onSent()
				})}
				subscription={{ submitting: true }}
			>
				{({ handleSubmit, submitting }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="4">
							<Grid gap="4">
								<Col sm={12} md={6}>
									{canChooseMailbox ? (
										<SelectField
											name="mailbox"
											label="Send From"
											select={SelectOutboundMailbox}
											required
											disabled={submitting}
										/>
									) : (
										<Stack gap="1">
											<Text fontSize="sm" fontWeight="semibold">
												Send From
											</Text>
											<Text color="muted">
												{composer?.suggested_mailbox
													? `${composer.suggested_mailbox.name} <${composer.suggested_mailbox.email}>`
													: "No mailbox available to send from."}
											</Text>
										</Stack>
									)}
								</Col>
								<Col>
									<Stack gap="1">
										<Text fontSize="sm" fontWeight="semibold">
											To
										</Text>
										<Text color="muted">
											{to
												? to.name
													? `${to.name} <${to.email}>`
													: to.email
												: "No recipient email is available."}
										</Text>
									</Stack>
								</Col>
							</Grid>
							<Stack gap="1">
								<TextInputField
									name="subject"
									label="Subject"
									required
									readOnly={isReply && !isEditingSubject}
									disabled={submitting}
									secondaryLabel={
										isReply && !isEditingSubject ? (
											<Button inline onClick={() => setIsEditingSubject(true)}>
												<Icons.Pencil size="3" />
											</Button>
										) : null
									}
									help={
										isReply && isEditingSubject ? (
											<Alert status="warning" inline>
												Some email clients may break the conversation thread if
												the reply subject is changed.
											</Alert>
										) : null
									}
								/>
							</Stack>
							<TextInputField
								name="cc"
								label="Cc"
								placeholder="email@example.com, other@example.com"
								help={({ value }) =>
									"Comma-separated email addresses to keep in the loop. " +
									(value
										? "<" +
											String(value)
												.split(",")
												.map((e) => String(e).trim())
												.filter(Boolean)
												.join(">,<") +
											">"
										: "")
								}
								disabled={submitting}
							/>
							{availableAttachments.length ? (
								<Stack gap="1">
									<Text fontSize="sm" fontWeight="semibold">
										Attachments
									</Text>
									<Inline gap="4" flexWrap="wrap">
										{availableAttachments.map((type) => {
											const size = attachmentSizes[type]
											const oversized = isOversizedAttachment(type)
											return (
												<CheckboxInputField
													key={type}
													name="attachments"
													value={type}
													label={
														ATTACHMENT_LABELS[type] +
														(size != null ? ` (${formatBytes(size)})` : "") +
														(oversized ? " — too large to attach" : "")
													}
													disabled={submitting || oversized}
												/>
											)
										})}
									</Inline>
								</Stack>
							) : null}
							<Stack gap="1">
								<SwitchInputField
									name="include_signature"
									label="Include my email signature"
									disabled={submitting || !hasSignature}
								/>
								{!hasSignature ? (
									<Text fontSize="sm" color="muted">
										You have no email signature yet.{" "}
										<Link
											to={`${generatePath("/settings")}#email-signature`}
											anchored
											color="accent"
										>
											Set up your email signature
										</Link>
										.
									</Text>
								) : null}
							</Stack>
							<Divider marginY="0" />
							<SubmissionError />
							<Inline gap="4">
								<Button type="submit" disabled={submitting}>
									<Icons.Mail /> {submitting ? "Sending..." : "Send Email"}
								</Button>
								<Button
									type="button"
									level="tertiary"
									onClick={onCancel}
									disabled={submitting}
								>
									Cancel
								</Button>
							</Inline>
						</Stack>
					</form>
				)}
			</Form>
		</Stack>
	)
}
